import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box, Link, Typography } from '@mui/material'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { ApiError } from '@/api/http-client'
import { queryClient } from '@/api/react-query'
import { useResendSignupCode, useValidateSignup } from '@/api/user-manager'
import { clearStoragePreservingAnnouncements, toast } from '@/helpers'
import { routes } from '@/router'
import { sessionButtonSx } from '../session-button'
import { SessionLegal, SessionScreen } from '../session-screen'
import { flowProgress } from '../profile/profile-steps'
import { useSignupDraft } from './use-signup-draft'

const RESEND_SECONDS = 30

export function SignupCodePage() {
  const navigate = useNavigate()
  const { draft } = useSignupDraft()
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS)

  const { mutateAsync: validateSignup, isPending } = useValidateSignup()
  const { mutateAsync: resendCode, isPending: isResending } =
    useResendSignupCode()
  const reconfirmRequested = useRef(false)

  useEffect(() => {
    if (reconfirmRequested.current) return
    if (!draft.email || !localStorage.getItem('access_token')) return

    reconfirmRequested.current = true
    clearStoragePreservingAnnouncements()
    queryClient.clear()
    resendCode({ email: draft.email }).catch(() => undefined)
  }, [draft.email, resendCode])

  useEffect(() => {
    if (secondsLeft <= 0) return

    const timeout = setTimeout(() => {
      setSecondsLeft((current) => current - 1)
    }, 1000)

    return () => clearTimeout(timeout)
  }, [secondsLeft])

  if (!draft.email) {
    return <Navigate to={routes.session.signup.email} replace />
  }

  async function confirm(token = code) {
    if (token.length < 4 || isPending) return

    try {
      const { access_token } = await validateSignup({
        email: draft.email,
        token_value: token,
      })
      localStorage.setItem('access_token', access_token)
      queryClient.removeQueries({ queryKey: ['user-profile'] })
      queryClient.removeQueries({ queryKey: ['session'] })
      toast.success('E-mail confirmado')
      navigate(routes.session.signup.password)
    } catch (error) {
      if (error instanceof ApiError) {
        setCodeError(
          'Código incorreto. Confira o e-mail ou peça um novo código.',
        )
        return
      }
    }
  }

  async function resend() {
    if (secondsLeft > 0 || isResending) return

    await resendCode({ email: draft.email })
    setSecondsLeft(RESEND_SECONDS)
    setCodeError('')
    toast.success('Enviamos um novo código')
  }

  return (
    <SessionScreen
      title="Vamos confirmar o seu e-mail"
      subtitle={
        <>
          Digite o código de 4 caracteres enviado para <b>{draft.email}</b>.{' '}
          <Link component={RouterLink} to={routes.session.signup.email}>
            Trocar e-mail
          </Link>
        </>
      }
      progress={flowProgress('code', null)}
      onBack={() => navigate(routes.session.signup.email)}
      footer={
        <>
          <LoadingButton
            size="large"
            fullWidth
            loading={isPending}
            disabled={code.length < 4}
            onClick={() => confirm()}
            sx={sessionButtonSx()}
          >
            Confirmar
          </LoadingButton>
          <SessionLegal />
        </>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          '& .MuiOtpInput-Box': { gap: 1.5, justifyContent: 'center' },
          '& .MuiOtpInput-TextField': { width: 64 },
          '& .MuiOutlinedInput-root': {
            bgcolor: codeError ? 'background.paper' : 'primary.50',
            borderRadius: 1.5,
          },
          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: codeError ? 'error.main' : 'text.secondary',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderColor: codeError ? 'error.main' : 'text.secondary',
              borderWidth: 2,
            },
          '& .MuiInputBase-input': {
            textAlign: 'center',
            fontSize: '1.4rem',
            fontWeight: 500,
            py: 1.5,
          },
        }}
      >
        <MuiOtpInput
          length={4}
          value={code}
          autoFocus
          onChange={(value) => {
            setCode(value.toUpperCase())
            setCodeError('')
          }}
          onComplete={(value) => confirm(value.toUpperCase())}
          validateChar={(character) => /^[a-zA-Z0-9]$/.test(character)}
          TextFieldsProps={{
            inputProps: {
              inputMode: 'text',
              autoCapitalize: 'characters',
              'aria-label': 'Código de confirmação',
            },
          }}
        />
        {codeError && (
          <Typography
            color="error"
            variant="body2"
            role="alert"
            textAlign="center"
            alignSelf="stretch"
          >
            {codeError}
          </Typography>
        )}
        <Typography textAlign="center" alignSelf="stretch">
          <Link
            component="button"
            type="button"
            disabled={secondsLeft > 0 || isResending}
            onClick={resend}
            sx={{
              color: secondsLeft > 0 ? 'text.disabled' : 'text.secondary',
            }}
          >
            {secondsLeft > 0
              ? `Reenviar código em 0:${String(secondsLeft).padStart(2, '0')}`
              : 'Reenviar código'}
          </Link>
        </Typography>
      </Box>
    </SessionScreen>
  )
}
